/**
 * Create User Modal — used from the Clients page (unassigned / internal tabs)
 * and from a client's Members section.
 *
 * mode 'client'   → creates a client-role user, optionally attached to an org.
 * mode 'internal' → creates a staff user (operations / sales / admin).
 */

import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { modal, useBreakpoint } from '../../pages/admin/UsersPage';

interface Props {
  mode: 'client' | 'internal';
  clients: any[];
  /** Pre-select (and lock) the organization, e.g. when opened from a client page. */
  defaultClientId?: string;
  lockClient?: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateUserModal({ mode, clients, defaultClientId = '', lockClient = false, onClose, onCreated }: Props) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(mode === 'internal' ? 'operations' : 'client');
  const [clientId, setClientId] = useState(defaultClientId);
  const [clientRole, setClientRole] = useState('viewer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) { setError('Email and password are required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await api.admin.createUser({
        email: email.trim(),
        password,
        name: name.trim() || undefined,
        role,
        clientId: mode === 'client' && clientId ? clientId : undefined,
        clientRole: mode === 'client' && clientId ? clientRole : undefined,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const clientName = clients.find((c) => c.id === clientId)?.name;

  return (
    <div style={modal.backdrop} onClick={onClose}>
      <form
        onSubmit={submit}
        style={{ ...modal.container, width: 480, ...(isMobile ? { width: '95vw' } : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h2 style={modal.title}>{mode === 'internal' ? 'New Team Member' : lockClient && clientName ? `New User for ${clientName}` : 'New Client User'}</h2>
          <button type="button" onClick={onClose} style={modal.closeBtn}>&times;</button>
        </div>

        <div style={modal.body}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} style={modal.input} placeholder="Full name" autoFocus />
            </label>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={modal.input} placeholder="name@company.com" required />
            </label>
            <label style={modal.fieldLabel}>
              <span style={modal.labelText}>Temporary password</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={modal.input} placeholder="At least 8 characters" required />
            </label>
            {mode === 'internal' ? (
              <label style={modal.fieldLabel}>
                <span style={modal.labelText}>Role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)} style={modal.input}>
                  <option value="operations">Operations</option>
                  <option value="sales">Sales</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
            ) : (
              <label style={modal.fieldLabel}>
                <span style={modal.labelText}>Organization</span>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={modal.input} disabled={lockClient}>
                  <option value="">Unassigned (fix later)</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            {mode === 'client' && clientId && (
              <label style={modal.fieldLabel}>
                <span style={modal.labelText}>Org role</span>
                <select value={clientRole} onChange={(e) => setClientRole(e.target.value)} style={modal.input}>
                  <option value="viewer">General Viewer</option>
                  <option value="commenter">User / Commenter</option>
                  <option value="people_manager">People Manager</option>
                  <option value="client_admin">Client Admin</option>
                </select>
              </label>
            )}
          </div>

          <div style={{ fontSize: 12, color: '#64748b', marginTop: 14, lineHeight: 1.5 }}>
            The user is created in Clerk too when Clerk is configured. Share the temporary password with them directly.
          </div>

          {error && <div style={{ ...modal.feedback, background: 'rgba(127, 29, 29, 0.6)', color: '#fca5a5' }}>{error}</div>}
        </div>

        <div style={modal.footer}>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={modal.cancelBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={modal.saveBtn}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
